import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaChevronDown } from "react-icons/fa";
import { SubscriptionUsersFilter } from "../subscription-users.service";
import { useTheme } from "src/context/theme.context";

type SelectOption = { value: string; text: string };
type SearchItemConfig = { type: "input" | "select"; placeholder: string; options?: SelectOption[] };

type Props = { 
  updateQuery: (filter: SubscriptionUsersFilter) => void;
  initialQuery?: SubscriptionUsersFilter;
  isSearching?: boolean;
};

const searchItems: Record<string, SearchItemConfig> = {
  subscriptionStatus: {
    type: "select",
    placeholder: "Choose subscription status",
    options: [
      { value: "ACTIVE", text: "Active" },
      { value: "CANCELLED", text: "Cancelled" },
      { value: "EXPIRED", text: "Expired" },
    ],
  },
  subscriptionProvider: {
    type: "select",
    placeholder: "Choose provider",
    options: [
      { value: "TAALY", text: "Taaly (Voucher)" },
      { value: "APPLE", text: "Apple" },
      { value: "GOOGLE", text: "Google" },
    ],
  },
  languageCode: {
    type: "input",
    placeholder: "Enter language code (e.g. en, nl)",
  },
};

export const SubscriptionUsersSearch = ({ updateQuery, initialQuery = {}, isSearching = false }: Props) => {
  const { isDarkMode } = useTheme();
  const items = Object.keys(searchItems) as (keyof typeof searchItems)[];
  const [selected, setSelected] = useState<keyof typeof searchItems>(items[0]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<SubscriptionUsersFilter>(initialQuery);
  const [activeBadges, setActiveBadges] = useState<{ key: keyof typeof searchItems; label: string; value: string }[]>(
    []
  );
  const ref = useRef<HTMLDivElement>(null);
  const previousSelectedRef = useRef<keyof typeof searchItems | null>(null);

  const currentConfig = searchItems[selected];
  const debounceRef = useRef<NodeJS.Timeout>();
  const hasActiveFilter = Object.values(currentFilters).some(Boolean);

  const handleSearch = useCallback(
    (key: keyof typeof searchItems, value: string) => {
      const newFilters: SubscriptionUsersFilter = { ...currentFilters };
      if (value) (newFilters as any)[key] = value;
      else delete (newFilters as any)[key];
      setCurrentFilters(newFilters);
      updateQuery(newFilters);

      // Update badges based on the new filters
      const badges: { key: keyof typeof searchItems; label: string; value: string }[] = [];
      
      Object.entries(newFilters).forEach(([filterKey, filterValue]) => {
        if (filterValue && searchItems[filterKey as keyof typeof searchItems]) {
          const searchKey = filterKey as keyof typeof searchItems;
          const config = searchItems[searchKey];
          const prettyValue = config.type === "select" 
            ? config.options?.find((o) => o.value === filterValue)?.text || filterValue
            : filterValue;
          badges.push({ key: searchKey, label: String(searchKey), value: prettyValue });
        }
      });
      
      setActiveBadges(badges);
    },
    [currentFilters, updateQuery]
  );

  const debounceSearch = useCallback(
    (key: keyof typeof searchItems, value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => handleSearch(key, value), 300);
    },
    [handleSearch]
  );

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Sync currentFilters with initialQuery when it changes
  useEffect(() => {
    setCurrentFilters(initialQuery);
    
    // Update badges based on current filters
    const badges: { key: keyof typeof searchItems; label: string; value: string }[] = [];
    
    Object.entries(initialQuery).forEach(([key, value]) => {
      if (value && searchItems[key as keyof typeof searchItems]) {
        const searchKey = key as keyof typeof searchItems;
        const config = searchItems[searchKey];
        const prettyValue = config.type === "select" 
          ? config.options?.find((o) => o.value === value)?.text || value
          : value;
        badges.push({ key: searchKey, label: String(searchKey), value: prettyValue });
      }
    });
    
    setActiveBadges(badges);
  }, [initialQuery]);

  useEffect(() => {
    if (previousSelectedRef.current !== null && previousSelectedRef.current !== selected) {
      setQuery("");
      handleSearch(selected, "");
    }
    previousSelectedRef.current = selected;
  }, [selected, handleSearch]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (currentConfig.type === "input") debounceSearch(selected, value.trim());
    else handleSearch(selected, value);
  };
  
  const renderInputField = () => {
    if (currentConfig.type === "select" && currentConfig.options) {
      return (
        <select
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`w-full px-6 py-4 bg-transparent text-lg border-none transition-colors duration-200 focus:outline-none ${
            isDarkMode ? "text-slate-100 focus:text-slate-100" : "text-slate-800 focus:text-emerald-700"
          }`}
        >
          <option value="">{currentConfig.placeholder}</option>
          {currentConfig.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      );
    }
    
    return (
      <input
        id="subscription-user-search-dropdown"
        type="search"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={currentConfig.placeholder}
        className={`w-full px-6 py-4 bg-transparent text-lg flex-grow border-none transition-colors duration-200 focus:border-none focus:outline-none focus:ring-0 ${
          isDarkMode
            ? "text-slate-100 placeholder-slate-400 focus:text-slate-100"
            : "text-slate-800 placeholder-slate-500 focus:text-emerald-700"
        }`}
      />
    );
  };

  return (
    <div className="flex items-center justify-center">
      <div className="w-full max-w-4xl mx-auto">
        <div ref={ref} className="relative group">
          <label htmlFor="subscription-user-search-dropdown" className="sr-only">
            Search category
          </label>

          <div
            className={`
              flex rounded-2xl overflow-hidden transition-all duration-300 ease-out
              backdrop-blur-sm border-2 shadow-xl
              ${
                focused || open
                  ? `${isDarkMode ? "border-emerald-400/60 bg-slate-900/95 shadow-2xl shadow-emerald-950/50 ring-4 ring-emerald-500/15" : "border-emerald-400 bg-white/95 shadow-2xl shadow-emerald-200/60 ring-4 ring-emerald-100/50"} scale-[1.02]`
                  : `${isDarkMode ? "border-slate-700 bg-slate-900/95 hover:border-emerald-500/40 hover:shadow-2xl" : "border-slate-200 bg-white/95 hover:border-emerald-300 hover:shadow-2xl"} hover:scale-[1.01]`
              }
            `}
          >
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className={`
                  flex items-center px-6 py-4 bg-gradient-to-r transition-all duration-200
                  ${
                    open
                      ? "from-emerald-500 via-teal-500 to-cyan-500 text-white shadow-lg"
                      : isDarkMode
                      ? "from-slate-800 via-slate-850 to-slate-800 text-slate-100 hover:from-emerald-500/20 hover:via-teal-500/20 hover:to-cyan-500/20 hover:text-emerald-200"
                      : "from-slate-100 via-slate-50 to-emerald-50 text-slate-700 hover:from-emerald-100 hover:via-teal-100 hover:to-cyan-100 hover:text-emerald-800"
                  }
                  ${isDarkMode ? "border-r border-slate-700" : "border-r border-slate-200"} font-medium 
                `}
            >
              <span className="truncate capitalize">{selected}</span>
              <FaChevronDown className={` ml-3 w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>

            <div className="flex-1 relative">
              {renderInputField()}
              {focused && (
                <div
                  className={`absolute inset-0 pointer-events-none border-none ${
                    isDarkMode
                      ? "bg-gradient-to-r from-emerald-500/5 via-teal-500/5 to-cyan-500/5"
                      : "bg-gradient-to-r from-emerald-50/40 via-teal-50/40 to-cyan-50/40"
                  }`}
                />
              )}
            </div>

            {isSearching && hasActiveFilter && (
              <div className="px-4 py-4 flex items-center justify-center text-emerald-500">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>

          <div
            className={`
              absolute top-full left-0 mt-2 z-50 transition-all duration-300 ease-out origin-top w-auto
              backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden
              ${isDarkMode ? "bg-slate-900/98 border border-slate-700" : "bg-white/98 border border-emerald-200"}
              ${open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"}
            `}
          >
            <ul className="pl-0 list-none m-0 text-sm">
              {items.map((item, index) => (
                <li key={item}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(item);
                      setOpen(false);
                    }}
                    className={`
                        w-full px-20 py-3 text-left transition-all duration-150 flex items-center block
                        ${
                          selected === item
                            ? isDarkMode
                              ? "bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 text-emerald-200 font-medium border-r-4 border-emerald-400 shadow-sm"
                              : "bg-gradient-to-r from-emerald-100 via-teal-100 to-cyan-100 text-emerald-800 font-medium border-r-4 border-emerald-400 shadow-sm"
                            : isDarkMode
                            ? "text-slate-200 hover:bg-gradient-to-r hover:from-emerald-500/10 hover:via-teal-500/10 hover:to-cyan-500/10 hover:text-emerald-200"
                            : "text-slate-700 hover:bg-gradient-to-r hover:from-emerald-50 hover:via-teal-50 hover:to-cyan-50 hover:text-emerald-700"
                        }
                      `}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <span className="flex-1 capitalize">{item}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {activeBadges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeBadges.map((b) => (
              <button
                key={`${b.key}-${b.value}`}
                onClick={() => handleSearch(b.key, "")}
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border 
                  ${
                    b.key === "subscriptionStatus"
                      ? b.value.toLowerCase() === "active"
                        ? isDarkMode
                          ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : b.value.toLowerCase() === "cancelled"
                        ? isDarkMode
                          ? "bg-red-500/15 text-red-200 border-red-500/30"
                          : "bg-red-50 text-red-700 border-red-200"
                        : isDarkMode
                        ? "bg-slate-700/60 text-slate-200 border-slate-600"
                        : "bg-gray-50 text-gray-700 border-gray-200"
                      : b.key === "subscriptionProvider"
                      ? isDarkMode
                        ? "bg-sky-500/15 text-sky-200 border-sky-500/30"
                        : "bg-blue-50 text-blue-700 border-blue-200"
                      : isDarkMode
                      ? "bg-cyan-500/15 text-cyan-200 border-cyan-500/30"
                      : "bg-cyan-50 text-cyan-700 border-cyan-200"
                  }
                `}
                title={`Remove ${b.label}`}
              >
                <span className="font-medium capitalize">{b.label}:</span>
                <span>{b.value}</span>
                <span className="ml-1">×</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionUsersSearch;
